import { ref } from 'vue'
import apiClient from '@/api/client'

export interface JurisdictionDepositLimits {
  daily_limit?: number
  monthly_limit?: number
}

export interface JurisdictionInfo {
  country_code: string | null
  region_code: string | null
  is_blocked: boolean
  blocked_games: string[]
  deposit_limits: JurisdictionDepositLimits | null
  bonus_restrictions: Record<string, unknown> | null
  required_kyc_tier: string | null
  allowed_payment_methods: string[] | null
}

// Module-level cache so the same data is shared across all component instances
// without requiring a Pinia store.
const cached = ref<JurisdictionInfo | null>(null)
const loading = ref(false)
const fetchError = ref<string | null>(null)

export function useJurisdiction() {
  async function fetchJurisdiction(): Promise<JurisdictionInfo | null> {
    if (cached.value !== null) return cached.value
    if (loading.value) return null

    loading.value = true
    fetchError.value = null

    try {
      const { data } = await apiClient.get<JurisdictionInfo>('/api/jurisdiction')
      cached.value = data
      return data
    } catch {
      fetchError.value = 'Unable to load jurisdiction information'
      return null
    } finally {
      loading.value = false
    }
  }

  /** Clear the cached jurisdiction data (e.g. after logout). */
  function resetJurisdiction(): void {
    cached.value = null
    fetchError.value = null
  }

  return {
    jurisdiction: cached,
    jurisdictionLoading: loading,
    jurisdictionError: fetchError,
    fetchJurisdiction,
    resetJurisdiction,
  }
}
